import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { RequireAuth } from './RequireAuth';
import { LoadingSpinner } from '@/components/LoadingSpinner/LoadingSpinner';
import { AppLayout } from '@/components/Layout/AppLayout';
import { useAuthTabSync } from '@/hooks/useAuth';

const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const SignupPage = lazy(() => import('@/features/auth/SignupPage'));
const AuthCallbackPage = lazy(() => import('@/features/auth/AuthCallbackPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const UsersPage = lazy(() => import('@/features/users/UsersPage'));
const PlanEditorPage = lazy(() => import('@/features/users/PlanEditorPage'));
const ProgramsPage = lazy(() => import('@/features/programs/ProgramsPage'));
const DrivePage = lazy(() => import('@/features/drive/DrivePage'));
const ActivityPage = lazy(() => import('@/features/activity/ActivityPage'));
const UserProgramWeeksPage = lazy(() => import('@/features/users/UserProgramWeeksPage'));
const ExercisesPage = lazy(() => import('@/features/exercises/ExercisesPage'));
const EquipmentPage = lazy(() => import('@/features/equipment/EquipmentPage'));
const PlanLabPage = lazy(() => import('@/features/adminEngineV2/PlanLabPage'));
const UserJourneyLabPage = lazy(() => import('@/features/adminEngineV2/UserJourneyLabPage'));
const AuditMatrixPage = lazy(() => import('@/features/adminEngineV2/AuditMatrixPage'));
const VitalLibraryPage = lazy(() => import('@/features/vitalLibrary/VitalLibraryPage'));
const BlenderRendersPage = lazy(() => import('@/features/renderPipeline/BlenderRendersPage'));
const HiggsfieldPage = lazy(() => import('@/features/renderPipeline/HiggsfieldPage'));
const NotFoundPage = lazy(() => import('@/features/errors/NotFoundPage'));

export const AppRouter = () => {
  // Watches for auth-storage changes from other tabs (sign-out in
  // another tab, or an unrelated tab detected the JWT died). Mounted
  // at the router level so it's active on every screen including
  // /login itself — the guard inside skips the redirect when we're
  // already there.
  useAuthTabSync();

  return (
    <Suspense fallback={<LoadingSpinner fullScreen />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />

        {/* Protected routes — nested under AppLayout */}
        <Route element={<RequireAuth />}>
          <Route element={<AppLayout />}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/users/:userId/plan-editor" element={<PlanEditorPage />} />
            <Route path="/users/:userId/program-weeks" element={<UserProgramWeeksPage />} />
            <Route path="/programs" element={<ProgramsPage />} />
            <Route path="/drive" element={<DrivePage />} />
            <Route path="/activity" element={<ActivityPage />} />

            {/* Program, Programs and Workouts were consolidated into
                /programs (two tabs) plus /drive and /activity. Both old
                paths are bookmarkable, so redirect rather than 404. */}
            <Route path="/workouts" element={<Navigate to="/programs" replace />} />
            <Route path="/program" element={<Navigate to="/programs" replace />} />
            <Route path="/exercises" element={<ExercisesPage />} />
            <Route path="/equipment" element={<EquipmentPage />} />
            <Route path="/admin/engine-v2/plan-lab" element={<PlanLabPage />} />
            <Route path="/admin/engine-v2/user-journey-lab" element={<UserJourneyLabPage />} />
            <Route path="/admin/engine-v2/audit-matrix" element={<AuditMatrixPage />} />
            <Route path="/admin/vital-library" element={<VitalLibraryPage />} />
            <Route path="/render-pipeline" element={<BlenderRendersPage />} />
            <Route path="/render-pipeline/higgsfield" element={<HiggsfieldPage />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};
